import { PayerDrift } from '@/lib/types';
import { cn } from '@/lib/utils';
import { AlertTriangle, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

interface PayerDriftRadarProps {
  data: PayerDrift[];
}

export function PayerDriftRadar({ data }: PayerDriftRadarProps) {
  const navigate = useNavigate();

  return (
    <div className="glass-card-elevated p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-warning" />
          <div>
            <h3 className="font-semibold text-sm">Payer Drift Radar</h3>
            <p className="text-xs text-muted-foreground">Behavior changes this week</p>
          </div>
        </div>
        <Button 
          variant="ghost" 
          size="sm" 
          className="gap-1 text-xs"
          onClick={() => navigate('/payer-intelligence')}
        >
          View All
          <ArrowRight className="w-3 h-3" />
        </Button>
      </div>

      <div className="space-y-2">
        {data.map((payer) => (
          <div
            key={payer.payer}
            onClick={() => navigate('/payer-intelligence')}
            className="p-3 rounded-lg bg-secondary/50 hover:bg-secondary transition-colors cursor-pointer"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3">
                <span className="font-medium text-sm">{payer.payer}</span>
                <div className={cn(
                  "px-2 py-0.5 rounded text-[10px] font-medium",
                  payer.driftScore >= 20 && "bg-warning/20 text-warning",
                  payer.driftScore >= 10 && payer.driftScore < 20 && "bg-amber-500/20 text-amber-400",
                  payer.driftScore < 10 && "bg-muted text-muted-foreground"
                )}>
                  Drift: {payer.driftScore}%
                </div>
              </div>
              <span className={cn(
                "font-mono text-sm font-medium",
                payer.impact < 0 ? "text-destructive" : "text-success"
              )}>
                {payer.impact < 0 ? '-' : '+'}${Math.abs(payer.impact).toLocaleString()}
              </span>
            </div>
            <p className="text-xs text-muted-foreground mb-1">{payer.topChange}</p>
            <p className="text-xs text-primary">{payer.suggestedFix}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
